/**
 * EventBus для коммуникации между Shell и микрофронтендами
 *
 * Основан на mitt - минималистичной библиотеке событий (~200 байт).
 * Используется для:
 * - Уведомления MF об изменениях платформы (тема, локаль, пользователь)
 * - Получения событий от MF (навигация, уведомления, ошибки)
 * - Межмодульной коммуникации
 */

import mitt from 'mitt'
import type { MFEventBus, MFEvents, PageContext } from './types'

/**
 * Создать новый EventBus
 */
export function createEventBus(): MFEventBus {
  return mitt<MFEvents>()
}

/**
 * Singleton EventBus для приложения
 */
let globalBus: MFEventBus | null = null

/**
 * Получить глобальный EventBus (создаёт при первом вызове)
 */
export function getEventBus(): MFEventBus {
  if (!globalBus) {
    globalBus = createEventBus()
  }
  return globalBus
}

/**
 * Сбросить глобальный EventBus (для тестов)
 */
export function resetEventBus(): void {
  if (globalBus) {
    globalBus.all.clear()
    globalBus = null
  }
}

/**
 * Типизированные хелперы для отправки событий платформы
 */
export const platformEvents = {
  /** Уведомить MF об изменении темы */
  themeChanged(bus: MFEventBus, theme: 'light' | 'dark'): void {
    bus.emit('platform:theme-changed', { theme })
  },

  /** Уведомить MF об изменении локали */
  localeChanged(bus: MFEventBus, locale: string): void {
    bus.emit('platform:locale-changed', { locale })
  },

  /** Уведомить MF об изменении пользователя */
  userUpdated(bus: MFEventBus, user: MFEvents['platform:user-updated']['user']): void {
    bus.emit('platform:user-updated', { user })
  },

  /** Уведомить MF об изменении маршрута */
  routeChanged(bus: MFEventBus, path: string, params?: Record<string, string>): void {
    bus.emit('platform:route-changed', { path, params })
  },

  /** Уведомить MF об изменении контекста страницы (для глобальных виджетов) */
  pageContextChanged(bus: MFEventBus, context: PageContext): void {
    bus.emit('platform:page-context', context)
  },
}

/**
 * Типизированные хелперы для отправки событий MF
 */
export const mfEvents = {
  /** MF успешно смонтирован */
  mounted(bus: MFEventBus, mfId: string, version: string): void {
    bus.emit('mf:mounted', { mfId, version })
  },

  /** MF размонтирован */
  unmounted(bus: MFEventBus, mfId: string): void {
    bus.emit('mf:unmounted', { mfId })
  },

  /** Ошибка в MF */
  error(bus: MFEventBus, mfId: string, error: string): void {
    bus.emit('mf:error', { mfId, error })
  },

  /** MF запрашивает навигацию */
  navigate(bus: MFEventBus, path: string): void {
    bus.emit('mf:navigate', { path })
  },

  /** MF отправляет уведомление */
  notification(
    bus: MFEventBus,
    type: 'success' | 'error' | 'warning' | 'info',
    message: string
  ): void {
    bus.emit('mf:notification', { type, message })
  },
}
